// src/services/centerEquipmentService.js
import { BASE_URL } from "../constants";
import { getAuthHeader } from "../utils";

export const centerEquipmentService = {
    
  /**
   * Obtiene la lista de equipos médicos de un centro
   * @param {number} centerId
   * @returns {Promise<Array>}
   */
getEquiposByCenter: async (centerId) => {
  const res = await fetch(`${BASE_URL}insurance/centers/${centerId}/equipos/`, {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al cargar los equipos');
  
  // ✅ Asegurar que siempre sea un array
  return Array.isArray(data.results) ? data.results : [];
},


  /**
   * Crea un nuevo equipo médico para un centro
   * @param {number} centerId
   * @param {Object} equipoData - { tipo_estudio: string, descripcion_equipo: string }
   * @returns {Promise<Object>}
   */
  createEquipo: async (centerId, equipoData) => {
    const res = await fetch(`${BASE_URL}insurance/centers/${centerId}/equipos/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(equipoData),
    });
    
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al crear el equipo');
    return data;
  },

  /**
   * Actualiza un equipo médico existente
   * @param {number} centerId
   * @param {number} equipoId
   * @param {Object} equipoData
   * @returns {Promise<Object>}
   */
  updateEquipo: async (centerId, equipoId, equipoData) => {
    const res = await fetch(`${BASE_URL}insurance/centers/${centerId}/equipos/${equipoId}/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(equipoData),
    });
    
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al actualizar el equipo');
    return data;
  },

  /**
   * Elimina un equipo médico
   * @param {number} centerId
   * @param {number} equipoId
   * @returns {Promise<void>}
   */
  deleteEquipo: async (centerId, equipoId) => {
    const res = await fetch(`${BASE_URL}insurance/centers/${centerId}/equipos/${equipoId}/`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeader(),
      },
    });
    
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Error al eliminar el equipo');
    }
    return { success: true };
  },

  /**
   * Obtiene los centros médicos del usuario autenticado
   */
  getUserMedicalCenters: async () => {
    const res = await fetch(`${BASE_URL}insurance/user/medical-centers/`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
    });

    if (!res.ok) {
      // Si no hay centros, el backend devuelve [] (200), no 404
      // Pero por seguridad, manejamos errores
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'No se pudieron cargar tus centros médicos');
    }

    const data = await res.json();
    return Array.isArray(data) ? data : [];
  },

  // SERVICIOS PARA TIPOS DE ESTUDIO
/**
 * Obtiene la lista de tipos de estudio
 * @returns {Promise<Array>}
 */
  getStudyTypes: async () => {
  const res = await fetch(`${BASE_URL}insurance/study-types/`, {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || 'Error al cargar tipos de estudio');
  }

  const data = await res.json();

  // ✅ Si la respuesta tiene 'results', úsala. Si no, usa el array directo.
  return Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []);
},

/**
 * Crea un nuevo tipo de estudio
 * @param {Object} studyTypeData - { name: string }
 * @returns {Promise<Object>} - el nuevo tipo creado
 */
createStudyType: async (studyTypeData) => {
  const res = await fetch(`${BASE_URL}insurance/study-types/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify(studyTypeData),
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || data.detail || 'Error al crear el tipo de estudio');
  }
  return data;
},

/**
 * Actualiza un tipo de estudio existente
 * @param {number} id
 * @param {Object} studyTypeData - { name: string }
 * @returns {Promise<Object>}
 */
updateStudyType: async (id, studyTypeData) => {
  const res = await fetch(`${BASE_URL}insurance/study-types/${id}/`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify(studyTypeData),
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || data.detail || 'Error al actualizar el tipo de estudio');
  }
  return data;
},

/**
 * Elimina un tipo de estudio
 * @param {number} id
 * @returns {Promise<void>}
 */
deleteStudyType: async (id) => {
  const res = await fetch(`${BASE_URL}insurance/study-types/${id}/`, {
    method: 'DELETE',
    headers: {
      ...getAuthHeader(),
    },
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || data.detail || 'Error al eliminar el tipo de estudio');
  }
  return { success: true };
},


/**
 * Obtiene los equipos médicos por modality_code
 * @param {string} modalityCode - Código DICOM como "MR", "CT", etc.
 * @returns {Promise<Object>} - Objeto con los datos del equipo
 */
getEquipoByModality: async (modalityCode) => {
  try {
    const params = new URLSearchParams({ modality: modalityCode });
    const res = await fetch(`${BASE_URL}insurance/equipment/by-modality/?${params}`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
    });

    if (!res.ok) {
      console.warn(`Error ${res.status} al obtener equipos para ${modalityCode}`);
      return { equipos: [] };
    }

    const data = await res.json();
    
    return data;
  } catch (error) {
    console.error('❌ Error en getEquipoByModality:', error);
    return { equipos: [] };
  }
},
};

